import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import WheelPreview from './components/WheelPreview';
import imageSrc from './imageSrc';
import {
  clearCampaign,
  fetchCampaign,
  deleteCampaign,
  openWheel,
  saveCampaign,
  uploadSegmentImage,
  setCampaignStatus,
  toggleSegment,
} from '../../redux/features/magicLampSlice';

/**
 * Magic Lamp — the segment editor
 *
 * The screen this feature exists for. Two things it is careful about:
 *
 *   - Chances are edited as percentages, with a running total. If they add to
 *     100, each number reads directly as "so many spins in every hundred".
 *   - The on/off switch saves immediately rather than waiting for Save. It is
 *     the control someone reaches for in a hurry when a prize runs out
 *     mid-event, and it must not depend on the rest of the form being valid.
 */
const COLORS = ['#8f1cab', '#ffb700', '#008ec3', '#6b21a8', '#e0a000', '#4bb4dc', '#4b0082', '#ffc933'];

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const { campaign: stored, loadingCampaign, saving } = useSelector((state) => state.magicLamp);

  // The wheel being edited is held locally while it is being worked on, and
  // only pushed to the store on save. Editing straight into redux would make
  // every keystroke a dispatch, and would leave a half-typed wheel in shared
  // state if the admin navigated away.
  const [campaign, setCampaign] = useState(null);
  const [segments, setSegments] = useState([]);
  const [dirty, setDirty] = useState(false);
  // Which segment's picture is currently uploading, so only that row shows a
  // spinner rather than the whole table locking up.
  const [uploadingFor, setUploadingFor] = useState(null);
  // The chances as they were before "Scale to 100%", so it can be undone. It
  // rewrites every row at once and the button then disappears — once the total
  // reads 100 there is nothing left to click — so without this the only way
  // back is to leave without saving and lose every other edit too.
  const [beforeScale, setBeforeScale] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(
    () =>
      dispatch(fetchCampaign(id))
        .unwrap()
        .catch((error) => enqueueSnackbar(error.message, { variant: 'error' })),
    [dispatch, id, enqueueSnackbar],
  );

  useEffect(() => {
    load();
    // Cleared on unmount so the editor never briefly shows the previous wheel.
    return () => dispatch(clearCampaign());
  }, [load, dispatch]);

  // Mirror the stored campaign into the local draft whenever the server sends a
  // fresh one — on load, after a save, and after a toggle.
  useEffect(() => {
    if (!stored) return;
    setCampaign(stored);
    setSegments(stored.segments || []);
    setDirty(false);
    // Whatever the server just sent is now the baseline, so an undo back to
    // pre-scale numbers would be undoing something already saved.
    setBeforeScale(null);
  }, [stored]);

  /**
   * Live odds from what is on screen, including unsaved edits, so the numbers
   * move as a chance is typed.
   */
  const odds = useMemo(() => {
    const drawable = segments.filter((s) => s.isActive && Number(s.chance) > 0);
    const total = drawable.reduce((sum, s) => sum + Number(s.chance || 0), 0);

    const now = new Map();
    segments.forEach((segment) => {
      const inPool = drawable.some((d) => d.id === segment.id);
      now.set(segment.id, total > 0 && inPool ? (Number(segment.chance) / total) * 100 : 0);
    });
    return now;
  }, [segments]);

  const isMadeUp = (campaign?.theme?.winnersSource || 'real') === 'custom';

  const totalChance = segments.reduce((sum, s) => sum + (Number(s.chance) || 0), 0);
  const roundedTotal = Math.round(totalChance * 10) / 10;
  const isHundred = Math.abs(roundedTotal - 100) < 0.05;

  const edit = (segmentId, key, value) => {
    setSegments((current) =>
      current.map((s) => (s.id === segmentId ? { ...s, [key]: value } : s)),
    );
    setDirty(true);
  };

  const addSegment = () => {
    const fallbackIndex = segments.findIndex((s) => s.isFallback);
    const next = {
      id: `new_${Date.now()}`,
      label: 'New prize',
      color: COLORS[segments.length % COLORS.length],
      chance: 10,
      imageKey: null,
      shownWins: null,
      isActive: true,
      isFallback: false,
      requiresApproval: false,
      wonCount: 0,
    };
    // Keep the fallback last on the wheel.
    const copy = [...segments];
    copy.splice(fallbackIndex === -1 ? copy.length : fallbackIndex, 0, next);
    setSegments(copy);
    setDirty(true);
  };

  /**
   * Uploads a picture for one segment and stores the returned URL.
   *
   * Checked client-side for type and size purely to fail fast with a clear
   * message — the shared uploader accepts video and audio up to 100MB, which is
   * not what belongs on a wheel slice.
   */
  const pickImage = async (segmentId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please choose an image file', { variant: 'warning' });
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      enqueueSnackbar('Please choose an image under 3MB', { variant: 'warning' });
      return;
    }

    setUploadingFor(segmentId);
    try {
      const key = await dispatch(uploadSegmentImage(file)).unwrap();
      edit(segmentId, 'imageKey', key);
      enqueueSnackbar('Picture added', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message || 'Upload failed', { variant: 'error' });
    } finally {
      setUploadingFor(null);
    }
  };

  /**
   * Deletes the whole campaign.
   *
   * The server refuses if any spins have been recorded — that history is the
   * record of what was actually given away, and deleting the campaign would
   * take it with it. In that case it tells you to set the campaign to Ended
   * instead, and that message is shown as-is.
   */
  const doDelete = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteCampaign(id)).unwrap();
      enqueueSnackbar('Campaign deleted', { variant: 'success' });
      navigate('/magic-lamp/spin');
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const removeSegment = (segmentId) => {
    setSegments((current) => current.filter((s) => s.id !== segmentId));
    setDirty(true);
  };

  const undoScale = () => {
    if (!beforeScale) return;
    setSegments(beforeScale);
    setBeforeScale(null);
    setDirty(true);
  };

  /** Rescales every chance so they total exactly 100, keeping their proportions. */
  const scaleTo100 = () => {
    if (totalChance <= 0) return;
    const previous = segments;
    const scaled = segments.map((s) => ({
      ...s,
      chance: Math.round((Number(s.chance) / totalChance) * 1000) / 10,
    }));
    // Put any rounding remainder on the largest slice so the total lands on
    // 100.0 rather than 99.9.
    const drift = Math.round((100 - scaled.reduce((sum, s) => sum + s.chance, 0)) * 10) / 10;
    if (drift !== 0) {
      const largest = scaled.reduce((a, b) => (b.chance > a.chance ? b : a));
      largest.chance = Math.round((largest.chance + drift) * 10) / 10;
    }
    setSegments(scaled);
    setBeforeScale(previous);
    setDirty(true);

    enqueueSnackbar('Chances scaled to 100%', {
      variant: 'success',
      autoHideDuration: 8000,
      action: (key) => (
        <Button
          size="small"
          sx={{ color: 'common.white' }}
          onClick={() => {
            setSegments(previous);
            setBeforeScale(null);
            setDirty(true);
            closeSnackbar(key);
          }}
        >
          Undo
        </Button>
      ),
    });
  };

  const save = async () => {
    try {
      await dispatch(
        saveCampaign({
          id,
          name: campaign.name,
          theme_json: campaign.theme,
          segments: segments.map((segment) => ({
            // A freshly added segment has a placeholder id; the server mints a
            // real one. Existing ids are kept so the spin log stays attached.
            id: String(segment.id).startsWith('new_') ? undefined : segment.id,
            label: segment.label,
            color: segment.color,
            imageKey: segment.imageKey || null,
            shownWins: segment.shownWins ?? null,
            chance: Number(segment.chance),
            isActive: segment.isActive,
            isFallback: segment.isFallback,
            requiresApproval: segment.requiresApproval,
          })),
        }),
      ).unwrap();
      enqueueSnackbar('Saved', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  /** Saves on the spot — a switch that needs confirming reads as broken. */
  const flip = async (segment) => {
    if (dirty) {
      enqueueSnackbar('Save your other changes first', { variant: 'warning' });
      return;
    }
    try {
      const res = await dispatch(
        toggleSegment({ campaignId: id, segmentId: segment.id, isActive: !segment.isActive }),
      ).unwrap();
      enqueueSnackbar(res.message, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const changeStatus = async (status) => {
    try {
      await dispatch(setCampaignStatus({ id, status })).unwrap();
      await load();
      enqueueSnackbar(`Campaign ${status}`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const openOnTablet = async () => {
    try {
      const res = await dispatch(openWheel(campaign.id)).unwrap();
      window.open(res.data.url, '_blank', 'noopener');
      enqueueSnackbar('Wheel opened. The link is valid for 5 minutes.', { variant: 'info' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  if (loadingCampaign && !campaign) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!campaign) return <Typography>Campaign not found.</Typography>;

  return (
    <GlobalWrapper title={campaign.name}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} mb={3} flexWrap="wrap">
        <Box>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/magic-lamp/spin')}>
            Campaigns
          </Button>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            /spin/{campaign.id}
          </Typography>
        </Box>

        <Stack direction="row" gap={1} flexWrap="wrap">
          {/* Short label, so the tooltip carries what it actually does — the
              link it opens is only valid for five minutes. */}
          <Tooltip title="Open the wheel on this device. The link lasts 5 minutes.">
            <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={openOnTablet}>
              Open
            </Button>
          </Tooltip>
          {campaign.status === 'active' ? (
            <Button variant="contained" color="warning" onClick={() => changeStatus('paused')}>
              Pause
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={() => changeStatus('active')}>
              Make live
            </Button>
          )}
          <Button variant="contained" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Tooltip title="Delete this campaign">
            <IconButton color="error" onClick={() => setConfirmingDelete(true)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card variant="outlined">
            {/*
              The segment table has seven columns and every one of them is
              editable, so it must not be allowed to squash. Below roughly
              720px it scrolls sideways instead — without this the right-hand
              columns are simply unreachable on a narrow window, and the
              on/off switch and delete button are the ones that fall off.
            */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={80}>Picture</TableCell>
                    <TableCell>Segment</TableCell>
                    <TableCell width={130}>Chance %</TableCell>
                    <TableCell width={90}>Actual</TableCell>
                    <TableCell width={70}>Won</TableCell>
                    <TableCell width={120}>Shown wins</TableCell>
                    <TableCell width={90}>On / off</TableCell>
                    <TableCell width={50} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {segments.map((segment) => (
                    <TableRow key={segment.id} sx={{ opacity: segment.isActive ? 1 : 0.6 }}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Tooltip title={segment.imageKey ? 'Change picture' : 'Add a picture'}>
                            <IconButton component="label" size="small" disabled={uploadingFor === segment.id}>
                              {uploadingFor === segment.id ? (
                                <CircularProgress size={28} />
                              ) : segment.imageKey ? (
                                <Avatar src={imageSrc(segment.imageKey)} sx={{ width: 34, height: 34 }} />
                              ) : (
                                <Avatar sx={{ width: 34, height: 34, bgcolor: 'grey.200' }}>
                                  <AddPhotoAlternateIcon fontSize="small" sx={{ color: 'grey.600' }} />
                                </Avatar>
                              )}
                              <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  pickImage(segment.id, e.target.files?.[0]);
                                  // Cleared so choosing the same file twice still fires onChange.
                                  e.target.value = '';
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          {segment.imageKey && (
                            <Tooltip title="Remove picture">
                              <IconButton size="small" onClick={() => edit(segment.id, 'imageKey', null)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <input
                            type="color"
                            value={segment.color || '#8f1cab'}
                            onChange={(e) => edit(segment.id, 'color', e.target.value)}
                            style={{
                              width: 26,
                              height: 26,
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              cursor: 'pointer',
                            }}
                          />
                          <TextField
                            variant="standard"
                            value={segment.label}
                            onChange={(e) => edit(segment.id, 'label', e.target.value)}
                            disabled={segment.isFallback}
                            sx={{ minWidth: 150 }}
                          />
                          {segment.isFallback && <Chip size="small" label="no win" />}
                          {!segment.isActive && <Chip size="small" color="default" label="off the wheel" />}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={segment.chance}
                          onChange={(e) => edit(segment.id, 'chance', e.target.value)}
                          inputProps={{ min: 0.1, step: 0.1, style: { textAlign: 'right' } }}
                          InputProps={{ endAdornment: <span style={{ color: '#94a3b8' }}>%</span> }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={700}>
                          {(odds.get(segment.id) ?? 0).toFixed(1)}%
                        </Typography>
                      </TableCell>

                      {/* A count of what has gone out, not a limit — nothing stops here. */}
                      <TableCell>{segment.wonCount ?? 0}</TableCell>

                      <TableCell>
                        <Tooltip
                          title={
                            isMadeUp
                              ? 'Claimed on the running strip instead of the real count. Blank leaves this prize off the strip.'
                              : 'Only used when the strip above is set to "Made up".'
                          }
                        >
                          <TextField
                            type="number"
                            size="small"
                            disabled={!isMadeUp || segment.isFallback}
                            value={segment.shownWins ?? ''}
                            placeholder="—"
                            onChange={(e) =>
                              edit(
                                segment.id,
                                'shownWins',
                                e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                              )
                            }
                            inputProps={{ min: 0, style: { textAlign: 'right' } }}
                            sx={{ width: 96 }}
                          />
                        </Tooltip>
                      </TableCell>



                      <TableCell>
                        {segment.isFallback ? (
                          <Typography variant="caption" color="text.secondary">
                            always on
                          </Typography>
                        ) : (
                          <Switch
                            checked={Boolean(segment.isActive)}
                            onChange={() => flip(segment)}
                            color="success"
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        {!segment.isFallback && (
                          <Tooltip title="Remove segment">
                            <IconButton size="small" onClick={() => removeSegment(segment.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <Stack direction="row" alignItems="center" gap={2} p={2} flexWrap="wrap">
              <Chip
                label={`Total ${roundedTotal}%`}
                color={isHundred ? 'success' : 'warning'}
                sx={{ fontWeight: 700 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                {isHundred
                  ? 'Each number is a real percentage — a 5 means 5 spins in every 100.'
                  : 'The wheel still works, but these are shares rather than percentages.'}
              </Typography>
              {!isHundred && totalChance > 0 && (
                <Button size="small" variant="outlined" onClick={scaleTo100}>
                  Scale to 100%
                </Button>
              )}
              {/* Stays until the change is saved or undone — the snackbar is
                  gone in eight seconds, and that is not long enough to look at
                  the wheel and decide. */}
              {beforeScale && (
                <Button size="small" color="inherit" onClick={undoScale}>
                  Undo scaling
                </Button>
              )}
              <Button size="small" startIcon={<AddIcon />} onClick={addSegment}>
                Add segment
              </Button>
            </Stack>

            <Box px={2} pb={2}>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Chance %</strong> is how often a prize comes up. <strong>Won</strong> is a
                running total of how many have gone out — not a limit, so nothing stops on its own.
                Switch a prize <strong>off</strong> when you run out of it: the segment stays visible
                on the wheel so visitors cannot tell, but it can no longer be won, and every other
                segment&rsquo;s chance rises to fill the gap. <strong>Actual</strong> is the real
                chance right now, which is why it can differ from what you typed.
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 16 }}>
            <Typography variant="overline" color="text.secondary">
              Live preview
            </Typography>
            <Box display="flex" justifyContent="center" mt={1}>
              <WheelPreview
                segments={segments}
                displayMode={campaign.theme?.displayMode}
                size={260}
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Settings
            </Typography>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Campaign name"
              value={campaign.name}
              onChange={(e) => {
                setCampaign({ ...campaign, name: e.target.value });
                setDirty(true);
              }}
            />

            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="display-mode-label">What each slice shows</InputLabel>
              <Select
                labelId="display-mode-label"
                label="What each slice shows"
                value={campaign.theme?.displayMode || 'image-text'}
                onChange={(e) => {
                  setCampaign({
                    ...campaign,
                    theme: { ...(campaign.theme || {}), displayMode: e.target.value },
                  });
                  setDirty(true);
                }}
              >
                <MenuItem value="image-text">Picture and name</MenuItem>
                <MenuItem value="image">Picture only — larger, no name</MenuItem>
                <MenuItem value="text">Name only — larger, no picture</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                Whichever you choose, a winner still sees the prize picture on the
                result screen if that prize has one.
              </Typography>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Running strip along the bottom"
              placeholder="Visit us at Stall 12 · Spin once per visitor · Good luck!"
              value={campaign.theme?.marquee || ''}
              onChange={(e) => {
                setCampaign({
                  ...campaign,
                  theme: { ...(campaign.theme || {}), marquee: e.target.value },
                });
                setDirty(true);
              }}
              helperText="Scrolls across the bottom of the tablet. Leave blank for no strip."
            />

            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="winners-source-label">Winners on the running strip</InputLabel>
              <Select
                labelId="winners-source-label"
                label="Winners on the running strip"
                value={campaign.theme?.winnersSource || 'real'}
                onChange={(e) => {
                  setCampaign({
                    ...campaign,
                    theme: { ...(campaign.theme || {}), winnersSource: e.target.value },
                  });
                  setDirty(true);
                }}
              >
                <MenuItem value="real">Real — counted from the spin log</MenuItem>
                <MenuItem value="custom">Made up — the number you set per prize</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                One line per prize: &ldquo;14 people have won the Smart TV&rdquo;.
                A wheel opening at nine in the morning honestly has an empty
                strip, which can read as nobody playing — the made-up option is
                for that. Names are never shown either way; the log records
                which prize was won, not who won it.
              </Typography>
            </FormControl>

            {(campaign.theme?.winnersSource || 'real') === 'custom' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Set each prize&rsquo;s number in the <strong>Shown wins</strong>
                &nbsp;column below. Leave one blank to keep it off the strip.
              </Alert>
            )}
          </Paper>

          {dirty && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You have unsaved changes.
            </Alert>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete this campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <strong>{campaign?.name}</strong> and all its segments will be removed.
            This cannot be undone.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            If anyone has already spun this wheel, deleting is blocked — set it to
            Ended instead, so the record of what was given away is kept.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmingDelete(false)}>Keep it</Button>
          <Button
            variant="contained"
            color="error"
            onClick={doDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </GlobalWrapper>
  );
}